import _ from "lodash";
import Api from "@/api";
import DeviceManagerStore from "@/store/modules/device-manager";
import { EventBus } from "@/event-bus";
import MutationTypes from "@/store/mutation-types";
import SpatVerificationData from "@/models/SpatVerificationData";
import StoreModule from "./store_module";
import TFMService from "@/api/service";

// The name of this store module.
const name = "spat-verification";

const startDateTime = new Date();
startDateTime.setMilliseconds(0);

// MQTT subscription names used in subscribing/unsubcribing from MQTT.
const MQTTSUBSCRIPTIONTSC = "SPAT_VERIFICATION_TSC";
const MQTTSUBSCRIPTIONRSU = "SPAT_VERIFICATION_RSU";

// Used to stop more than one EventBus subscription for receiving MQTT messages.
let subscribedOnce = false;

// Used to hold a timer to unsubscribe from MQTT.
let stopSourceTimer = 0;

// Used to hold a timer to unset data received property.
let dataReceivedTimerTsc = 0;
let dataReceivedTimerRsu = 0;

/**
 * The time span in minutes that the RSU for the
 * selected intersection was last heard.
 */
const timeSpanMinutesRsuLastHeard = 1;

/**
 * Helper function to set & unset data received.
 * @param commit (object)
 */
const dataReceived = function(commit, deviceType) {
	switch (deviceType) {
		case "TSC":
			if (dataReceivedTimerTsc === 0) {
				commit("SET_DATA_RECEIVED", { deviceType: "TSC", value: true });
				dataReceivedTimerTsc = window.setTimeout(() => {
					commit("SET_DATA_RECEIVED", { deviceType: "TSC", value: false });
					dataReceivedTimerTsc = 0;
				}, 25);
			}
			break;
		case "RSU":
			if (dataReceivedTimerRsu === 0) {
				commit("SET_DATA_RECEIVED", { deviceType: "RSU", value: true });
				dataReceivedTimerRsu = window.setTimeout(() => {
					commit("SET_DATA_RECEIVED", { deviceType: "RSU", value: false });
					dataReceivedTimerRsu = 0;
				}, 25);
			}
			break;
		default:
			break;
	}
};

const actions = {
	/**
	 * Handles inserting new SpatVerificationData object.
	 * @param payload (SpatVerificationData)
	 *
	 * @returns void
	 */
	ADD_DATA_ROW({ state, commit }, payload) {
		if (state.transport.waitingForData === true) {
			commit("SET_WAITING_FOR_DATA", false);
		}
		commit("ADD_DATA_ROW", payload);
		if (payload.windowId === "rsu") {
			commit("SET_MAX_DISPLAY_FRAME_NUMBER", payload.data.frameNumber);
		}
	},

	/**
	 * Retrieves list of sources that have received SPaTs from
	 * given txRsu device.
	 * @param txRsu (object)
	 */
	async FETCH_SOURCES({ rootGetters, commit }, { txRsu }) {
		console.log("FETCH_SOURCES for txRSU: ", txRsu);

		let radioNeighbors = await Api.getWithParamsJson(
			Api.ENDPOINTS.GET_RSU_RADIO_NEIGHBORS_BY_USER,
			{
				rsuId: txRsu.deviceId,
				timeSpanMinutes: timeSpanMinutesRsuLastHeard
			}
		).catch(() => {
			return Promise.reject(
				"There was a network problem while searching for verification sources."
			);
		});

		if (
			!radioNeighbors ||
			!radioNeighbors["rows"] ||
			!radioNeighbors["rows"].length > 0
		) {
			return Promise.reject("No verification sources were found.");
		}

		// List of all user's devices.
		const rsuDevices = rootGetters[
			DeviceManagerStore.MODULE_NAME + "/devicesOfType"
		](txRsu.$deviceTypeId);

		// Filter out devices that user does not have in their device list, to
		// give available sources.
		const availableSources = radioNeighbors["rows"].filter(
			row => rsuDevices[row.deviceId]
		);
		console.log("available sources", availableSources);

		const availableVerification = [];
		let promises = [];
		_.each(availableSources, source => {
			// TODO: This is a temporary fix to add ip address for each available source.
			// It only needs to be a promise because of having to reach out for IP address
			// separately for each available source. This will do it in parallel, but still
			// less than ideal.
			// const promise = new Promise(async resolve => {
			const device = rsuDevices[source.deviceId];
			let promise = TFMService.getIpAddressByDeviceId(device.deviceId).then(
				ipAddressInfo => {
					if (device != undefined) {
						availableVerification.push({
							label:
								"#" +
								device.deviceId.toString() +
								" " +
								device.locationDescription,
							deviceId: device.deviceId,
							ipAddress: ipAddressInfo ? ipAddressInfo["ipAddress"] : ""
						});
					}
				}
			);
			// });
			promises.push(promise);
		});
		await Promise.all(promises).catch(() => {
			return Promise.reject("Could not determine verification sources.");
		});

		if (availableVerification.length === 0) {
			return Promise.reject("No verification sources were found.");
		}
		commit("SET_AVAILABLE_SOURCES", { availableVerification });
	},

	/**
	 * Handles subscription to MQTT and EventBus and sends instruction
	 * for parsers to push data onto MQTT topics.
	 * @param txTsc (object)
	 * @param txRsu (object)
	 * @param rxRsu (object)
	 *
	 * @returns Promise
	 */
	async START_SOURCE(
		{ state, commit, dispatch, rootGetters },
		{ txTsc, txRsu, rxRsu }
	) {
		const allRsuDevices = rootGetters[
			DeviceManagerStore.MODULE_NAME + "/devicesOfType"
		](txRsu.$deviceTypeId);

		const tscDevice = txTsc;
		const rsuDevice = allRsuDevices[rxRsu.deviceId];

		if (!state.sources.sourcesLoaded) {
			return Promise.reject("Sources are not loaded.");
		}
		commit("SET_WAITING_FOR_DATA", true);
		console.log("START_SOURCE", tscDevice, rsuDevice);

		if (stopSourceTimer != 0) {
			window.clearTimeout(stopSourceTimer);
			stopSourceTimer = 0;
		}

		// Publish query to MQTT for TSC data
		const post1 = Api.postJson(
			Api.ENDPOINTS.MQTT_PUBLISH,
			{
				Topic: "v1/spat/signalcontroller/listen/query",
				Payload: JSON.stringify({ ControllerId: tscDevice.deviceId }),
				QualityOfService: 1,
				Retain: false
			},
			{},
			() => {
				return Promise.reject("Failed to make TSC data connection.");
			}
		);

		// Publish query to MQTT for RSU data
		// TODO: FIGURE OUT HOW TO GET REGION ID CORRECTLY BEFORE RELEASE TO PRODUCTION
		const rsuIntersectionId = txRsu["rsuIntersectionId"];
		const rsuRegionId = -1;
		const post2 = Api.postJson(
			Api.ENDPOINTS.MQTT_PUBLISH,
			{
				Topic: "v1/spat/j2735/listen/query",
				Payload: JSON.stringify({
					intersectionId: rsuIntersectionId,
					regionId: rsuRegionId
				}),
				QualityOfService: 1,
				Retain: false
			},
			{},
			() => {
				return Promise.reject("Failed to make RSU data connection.");
			}
		);

		await Promise.all([post1, post2]).catch(content => {
			commit("SET_WAITING_FOR_DATA", false);
			commit("SET_IS_PLAYING", false);
			return Promise.reject(content);
		});

		console.log("IP Address", state.sources.rxIpAddress);

		const tscTopic = `v1/spat/signalcontroller/listen/${tscDevice.deviceId}/data`;

		const rsuTopic = `v1/spat/j2735/listen/${rsuIntersectionId}_${rsuRegionId}/data`;

		commit(
			MutationTypes.MQTT_SUBSCRIBE,
			{
				name: MQTTSUBSCRIPTIONTSC,
				qos: 0,
				subscription: tscTopic
			},
			{ root: true }
		);

		commit(
			MutationTypes.MQTT_SUBSCRIBE,
			{
				name: MQTTSUBSCRIPTIONRSU,
				qos: 0,
				subscription: rsuTopic
			},
			{ root: true }
		);

		// We only need to add this event callback once
		if (subscribedOnce === false) {
			EventBus.onGlobalEvent("mqtt-client:messageArrived", message => {
				if (message["message"]["topic"] === tscTopic) {
					new Promise(() => {
						if (message["message"] && message["message"]["payloadString"]) {
							const dataRow = SpatVerificationData.parseTscMessage(message);
							dispatch("ADD_DATA_ROW", {
								windowId: "tsc",
								data: dataRow
							});
							dataReceived(commit, "TSC");
						}
					});
				}

				if (message["message"]["topic"] === rsuTopic) {
					new Promise(() => {
						// Only commit messages that have Ip equal
						// to the selected verification source device Ip.
						// Parse the payloadString first, then
						// commit if IP matches.
						if (message["message"] && message["message"]["payloadString"]) {
							const dataRow = SpatVerificationData.parseRsuMessage(message);
							if (dataRow.ipAddress === state.sources.rxIpAddress) {
								dispatch("ADD_DATA_ROW", {
									windowId: "rsu",
									data: dataRow
								});
							}
							dataReceived(commit, "RSU");
						}
					});
				}
			});
		}

		subscribedOnce = true;
		return Promise.resolve();
	},

	/**
	 * Stops source data after a fixed period of time
	 */
	STOP_SOURCE({ commit, dispatch }) {
		commit("SET_WAITING_FOR_DATA", false);
		stopSourceTimer = window.setTimeout(
			() => {
				dispatch("STOP_SOURCE_IMMEDIATELY");
			},
			300000 // 5 mins = 300000
		);
	},

	/**
	 * Stops source data immediately.
	 */
	STOP_SOURCE_IMMEDIATELY({ commit }) {
		console.log("STOP_SOURCE");
		commit("SET_WAITING_FOR_DATA", false);
		commit(MutationTypes.MQTT_UNSUBSCRIBE, MQTTSUBSCRIPTIONTSC, {
			root: true
		});
		commit(MutationTypes.MQTT_UNSUBSCRIBE, MQTTSUBSCRIPTIONRSU, {
			root: true
		});
		stopSourceTimer = 0;

		commit("SET_IS_PLAYING", false);
	}
};

const mutations = {
	// TRANSPORT
	// Make sure you have set max display time before trying to
	// set the current frame number
	SET_CURRENT_FRAME_NUMBER(state, payload) {
		if (
			payload >= state.transport.minDisplayFrameNumber &&
			payload <= state.transport.maxDisplayFrameNumber
		) {
			state.transport.currentFrameNumber = payload;
		}
	},
	SET_DATA_RECEIVED(state, { deviceType, value }) {
		state.transport.dataReceived[deviceType] = value ? true : false;
	},
	SET_MAX_DISPLAY_FRAME_NUMBER(state, payload) {
		if (payload > state.transport.maxDisplayFrameNumber) {
			state.transport.maxDisplayFrameNumber = payload;
		}
	},
	SET_MIN_DISPLAY_FRAME_NUMBER(state, payload) {
		if (payload <= state.transport.maxDisplayFrameNumber) {
			state.transport.minDisplayFrameNumber = payload;
		}
	},
	SET_IS_PLAYING(state, payload) {
		state.transport.isPlaying = payload ? true : false;
	},
	SET_TRANSPORT_SPEED(state, payload) {
		// Just don't let payload equal 0.
		if (payload === 0) {
			payload = +1;
		}
		state.transport.transportSpeed = payload;
	},
	SET_WAITING_FOR_DATA(state, payload) {
		state.transport.waitingForData = payload;
	},
	// SOURCES
	SET_AVAILABLE_SOURCES(state, payload) {
		// console.log("SET_SOURCES", payload);
		// TODO: Add check here that sources are well formed?
		if (payload["currentTsc"]) {
			state.sources.currentTsc = payload["currentTsc"];
		}
		if (payload["availableVerification"]) {
			state.sources.availableVerification = payload["availableVerification"];
		}
		if (
			state.sources.currentTsc["label"] &&
			state.sources.availableVerification.length > 0
		) {
			state.sources.sourcesLoaded = true;
		} else {
			state.sources.sourcesLoaded = false;
		}
	},
	SET_VERIFICATION_SOURCE(state, payload) {
		state.sources.selectedVerification = payload;
	},
	SET_RX_IP_ADDRESS(state, payload) {
		if (typeof payload === "string") {
			state.sources.rxIpAddress = payload;
		}
	},
	// OFFSET
	SET_OFFSET_TIME(state, payload) {
		if (payload < 0 || payload.toString().length === 0) {
			payload = 0;
		}
		if (payload > 2000) {
			payload = 2000;
		}
		state.offset.offsetTime = payload;
	},
	SET_IS_LINKED(state, payload) {
		state.offset.isLinked = payload ? true : false;
	},
	SET_IS_AUTO(state, payload) {
		state.offset.isAuto = payload ? true : false;
	},
	ADD_PHASE_CHANGE_FRAME(state, payload) {
		state.offset.phaseChangeFrameNumbers.push(payload);
	},
	// DATAWINDOWS
	ADD_DATA_ROW(state, payload) {
		let data = {};
		if (payload["data"] instanceof SpatVerificationData) {
			data = payload["data"];
		} else if (typeof payload["data"] === "object") {
			data = new SpatVerificationData(payload["data"]);
		} else {
			return;
		}

		const frameNumber = data.frameNumber;
		const windowId = payload["windowId"];

		// Add an entry in the frames object for the frame and index of the data
		// row just added. Only add one SpatVerificationData per frame per windowId.
		if (!state.dataWindows[windowId].frames[frameNumber]) {
			state.dataWindows[windowId].frames[frameNumber] = data;
		}

		// Add latest data row
		state.dataWindows[windowId].latestDataRow = data;
	},
	SET_PHASE_NUMBER_TSC(state, payload) {
		if (payload > 0 && payload < 17) {
			state.dataWindows.tsc.phaseNumber = payload;
		}
	},
	SET_PHASE_NUMBER_RSU(state, payload) {
		if (payload > 0 && payload < 17) {
			state.dataWindows.rsu.phaseNumber = payload;
		}
	},
	CLEAR_DATA_WINDOWS(state) {
		state.dataWindows.tsc.dataRows = [];
		state.dataWindows.tsc.frames = {};
		state.dataWindows.rsu.dataRows = [];
		state.dataWindows.rsu.frames = {};
		state.offset.phaseChangeFrameNumbers = [];
		console.log("CLEAR_DATA_WINDOWS", state);
	},
	// MAP VISUALIZERS
	SET_CENTER_LAT_LONG(state, { latitude, longitude }) {
		state.mapVisualizer.center.latitude = latitude;
		state.mapVisualizer.center.longitude = longitude;
	},
	SET_INTERSECTION(state, intersection) {
		state.mapVisualizer.intersection = intersection;
	},
	// RESET
	RESET(state) {
		Object.assign(state, initialState);
	}
};

const getters = {
	dataWindows(state) {
		return state.dataWindows;
	},
	getDataRowForFrameRsu: state => frameNumber => {
		let dataRow = null;
		const dataRowRetrieved = state.dataWindows.rsu.frames[frameNumber];
		if (dataRowRetrieved) {
			dataRow = dataRowRetrieved;
		}
		return dataRow;
	},
	getDataRowForFrameTsc: state => frameNumber => {
		let dataRow = null;
		const dataRowRetrieved = state.dataWindows.tsc.frames[frameNumber];
		if (dataRowRetrieved) {
			dataRow = dataRowRetrieved;
		}
		return dataRow;
	},
	mapVisualizer(state) {
		return state.mapVisualizer;
	},
	offset(state) {
		return state.offset;
	},
	sources(state) {
		return state.sources;
	},
	transport(state) {
		return {
			...state.transport,
			timecode: SpatVerificationData.dateToTimecode(
				new Date(state.transport.currentFrameNumber)
			)
		};
	}
};

// Use this to set currentFrameNumber to all zeros.
const localMidnight = (function() {
	let now = new Date();
	now.setHours(0);
	now.setMinutes(0);
	now.setSeconds(0);
	now.setMilliseconds(0);
	return now.getTime();
})();

const initialState = {
	dataWindows: {
		tsc: {
			latestDataRow: null,
			frames: {},
			phaseNumber: 2
		},
		rsu: {
			latestDataRow: null,
			frames: {},
			phaseNumber: 2
		}
	},
	mapVisualizer: {
		center: {
			latitude: null,
			longitude: null
		},
		intersection: {}
	},
	offset: {
		offsetTime: 0,
		isLinked: true,
		isAuto: true,
		phaseChangeFrameNumbers: []
	},
	sources: {
		currentTsc: {},
		selectedVerification: 0,
		availableVerification: [],
		sourcesLoaded: false,
		rxIpAddress: ""
	},
	transport: {
		dataReceived: { TSC: false, RSU: false },
		isPlaying: false,
		currentFrameNumber: localMidnight,
		maxDisplayFrameNumber: 0,
		minDisplayFrameNumber: 0,
		transportSpeed: 1,
		waitingForData: false
	},
	subscriptions: {}
};

/**
 * Create the new module.
 * Clone initialState object so that we maintain a "clean" baseline.
 */
const SpatVerificationModule = new StoreModule(
	name,
	mutations,
	actions,
	getters,
	_.cloneDeep(initialState)
);

export default SpatVerificationModule;
